import { useState, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useTripAttachments,
  useUploadAttachment,
  useDeleteAttachment,
  useLinkAttachment,
  useAttachmentUrl,
  type ItemType,
  type Attachment,
} from "@/hooks/useAttachments";
import { Trip, TripData } from "@/hooks/useTrips";
import {
  FileText,
  Image,
  Download,
  Trash2,
  Upload,
  Eye,
  Loader2,
  Paperclip,
  ChevronDown,
  ChevronRight,
  Plane,
  Hotel,
  Car,
  Ship,
  Calendar,
  Link2,
  Unlink,
  MoreHorizontal,
} from "lucide-react";

interface AttachmentsManagerDialogProps {
  trip: Trip;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith("image/")) {
    return <Image className="h-4 w-4 text-blue-500" />;
  }
  if (contentType === "application/pdf") {
    return <FileText className="h-4 w-4 text-red-500" />;
  }
  return <FileText className="h-4 w-4 text-gray-500" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface AttachmentRowProps {
  attachment: Attachment;
  tripData: TripData | null;
  onUnlink?: () => void;
  onLink?: (itemType: ItemType, itemIndex: number) => void;
  showLinkOptions?: boolean;
}

function AttachmentRow({
  attachment,
  tripData,
  onUnlink,
  onLink,
  showLinkOptions = false,
}: AttachmentRowProps) {
  const { data: signedUrl, isLoading: urlLoading } = useAttachmentUrl(attachment.storage_path);
  const deleteAttachment = useDeleteAttachment();
  const [showPreview, setShowPreview] = useState(false);

  const handleView = () => {
    if (signedUrl) {
      if (attachment.content_type.startsWith("image/")) {
        setShowPreview(true);
      } else {
        window.open(signedUrl, "_blank");
      }
    }
  };

  const handleDownload = () => {
    if (signedUrl) {
      const a = document.createElement("a");
      a.href = signedUrl;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this attachment?")) {
      deleteAttachment.mutate(attachment);
    }
  };

  // Build link options from trip data
  const linkOptions = useMemo(() => {
    if (!tripData) return [];
    const options: { type: ItemType; index: number; label: string }[] = [];

    tripData.flights?.forEach((f, i) => {
      options.push({
        type: "flights",
        index: i,
        label: `${f.airline} ${f.flight_number} - ${f.departure_airport} to ${f.arrival_airport}`,
      });
    });

    tripData.hotels?.forEach((h, i) => {
      options.push({
        type: "hotels",
        index: i,
        label: h.hotel_name || "Hotel",
      });
    });

    tripData.cars?.forEach((c, i) => {
      options.push({
        type: "cars",
        index: i,
        label: `${c.rental_company} - ${c.pickup_location}`,
      });
    });

    tripData.cruises?.forEach((cr, i) => {
      options.push({
        type: "cruises",
        index: i,
        label: `${cr.cruise_line} - ${cr.ship_name}`,
      });
    });

    tripData.activities?.forEach((a, i) => {
      options.push({
        type: "activities",
        index: i,
        label: a.name,
      });
    });

    return options;
  }, [tripData]);

  return (
    <>
      <div className="flex items-center gap-2 py-2 px-2 hover:bg-muted/50 rounded-lg transition-colors group">
        {getFileIcon(attachment.content_type)}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{attachment.filename}</p>
          <p className="text-xs text-muted-foreground">
            {formatFileSize(attachment.file_size)} &middot;{" "}
            {attachment.source === "email" ? "Email" : "Upload"}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleView}
              disabled={urlLoading}
              title="View"
            >
              {urlLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleDownload}
              disabled={urlLoading}
              title="Download"
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {showLinkOptions && linkOptions.length > 0 && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Link2 className="h-4 w-4 mr-2" />
                    Link to item
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-60 overflow-y-auto">
                    {linkOptions.map((opt) => (
                      <DropdownMenuItem
                        key={`${opt.type}-${opt.index}`}
                        onClick={() => onLink?.(opt.type, opt.index)}
                      >
                        {opt.type === "flights" && <Plane className="h-3 w-3 mr-2" />}
                        {opt.type === "hotels" && <Hotel className="h-3 w-3 mr-2" />}
                        {opt.type === "cars" && <Car className="h-3 w-3 mr-2" />}
                        {opt.type === "cruises" && <Ship className="h-3 w-3 mr-2" />}
                        {opt.type === "activities" && <Calendar className="h-3 w-3 mr-2" />}
                        <span className="truncate max-w-[200px]">{opt.label}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {onUnlink && (
                <DropdownMenuItem onClick={onUnlink}>
                  <Unlink className="h-4 w-4 mr-2" />
                  Unlink from item
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={handleDelete}
                disabled={deleteAttachment.isPending}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Image Preview Dialog */}
      {showPreview && signedUrl && (
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{attachment.filename}</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center">
              <img
                src={signedUrl}
                alt={attachment.filename}
                className="max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

interface ItemSectionProps {
  title: string;
  icon: React.ReactNode;
  items: { name: string; index: number; attachments: Attachment[] }[];
  tripData: TripData | null;
  tripId: string;
  itemType: ItemType;
}

function ItemSection({ title, icon, items, tripData, tripId, itemType }: ItemSectionProps) {
  const [isOpen, setIsOpen] = useState(true);
  const linkAttachment = useLinkAttachment();

  const totalAttachments = items.reduce((sum, item) => sum + item.attachments.length, 0);

  if (items.length === 0) return null;

  const handleUnlink = (attachmentId: string) => {
    linkAttachment.mutate({
      attachmentId,
      tripId,
      itemType: null,
      itemIndex: null,
    });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 hover:bg-muted/50 rounded-lg px-2 transition-colors">
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {icon}
        <span className="font-medium text-sm">{title}</span>
        {totalAttachments > 0 && (
          <Badge variant="secondary" className="ml-auto">
            {totalAttachments}
          </Badge>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-6 space-y-2 mt-1">
        {items.map((item) => (
          <div key={item.index} className="space-y-1">
            <p className="text-xs text-muted-foreground font-medium px-2">{item.name}</p>
            {item.attachments.length > 0 ? (
              item.attachments.map((att) => (
                <AttachmentRow
                  key={att.id}
                  attachment={att}
                  tripData={tripData}
                  onUnlink={() => handleUnlink(att.id)}
                />
              ))
            ) : (
              <p className="text-xs text-muted-foreground px-2 py-1 italic">No attachments</p>
            )}
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function AttachmentsManagerDialog({
  trip,
  open,
  onOpenChange,
}: AttachmentsManagerDialogProps) {
  const tripData = trip.trip_data as TripData | null;
  const { data: allAttachments, isLoading } = useTripAttachments(trip.id);
  const uploadAttachment = useUploadAttachment();
  const linkAttachment = useLinkAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Group attachments by item
  const groupedAttachments = useMemo(() => {
    if (!allAttachments) return { unlinked: [], flights: [], hotels: [], cars: [], cruises: [], activities: [] };

    const unlinked = allAttachments.filter((a) => !a.item_type);

    const getForItem = (type: ItemType, index: number) =>
      allAttachments.filter((a) => a.item_type === type && a.item_index === index);

    const flights = (tripData?.flights || []).map((f, i) => ({
      name: `${f.airline} ${f.flight_number} - ${f.departure_airport} to ${f.arrival_airport}`,
      index: i,
      attachments: getForItem("flights", i),
    }));

    const hotels = (tripData?.hotels || []).map((h, i) => ({
      name: h.hotel_name || "Hotel",
      index: i,
      attachments: getForItem("hotels", i),
    }));

    const cars = (tripData?.cars || []).map((c, i) => ({
      name: `${c.rental_company} - ${c.pickup_location}`,
      index: i,
      attachments: getForItem("cars", i),
    }));

    const cruises = (tripData?.cruises || []).map((cr, i) => ({
      name: `${cr.cruise_line} - ${cr.ship_name}`,
      index: i,
      attachments: getForItem("cruises", i),
    }));

    const activities = (tripData?.activities || []).map((a, i) => ({
      name: a.name,
      index: i,
      attachments: getForItem("activities", i),
    }));

    return { unlinked, flights, hotels, cars, cruises, activities };
  }, [allAttachments, tripData]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    await uploadAttachment.mutateAsync({
      file,
      tripId: trip.id,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleLink = (attachmentId: string, itemType: ItemType, itemIndex: number) => {
    linkAttachment.mutate({
      attachmentId,
      tripId: trip.id,
      itemType,
      itemIndex,
    });
  };

  const totalCount = allAttachments?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0 pr-8">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5" />
              Trip Attachments
              {totalCount > 0 && (
                <Badge variant="secondary">{totalCount}</Badge>
              )}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 min-h-0 pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : totalCount === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Paperclip className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No attachments yet</p>
              <p className="text-sm mt-1">Upload files or forward emails with attachments</p>
            </div>
          ) : (
            <>
              {/* Unlinked Attachments */}
              {groupedAttachments.unlinked.length > 0 && (
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 hover:bg-muted/50 rounded-lg px-2 transition-colors">
                    <ChevronDown className="h-4 w-4" />
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Unlinked Attachments</span>
                    <Badge variant="outline" className="ml-auto">
                      {groupedAttachments.unlinked.length}
                    </Badge>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-6 space-y-1 mt-1">
                    {groupedAttachments.unlinked.map((att) => (
                      <AttachmentRow
                        key={att.id}
                        attachment={att}
                        tripData={tripData}
                        showLinkOptions
                        onLink={(type, index) => handleLink(att.id, type, index)}
                      />
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Item Sections */}
              <ItemSection
                title="Flights"
                icon={<Plane className="h-4 w-4 text-blue-500" />}
                items={groupedAttachments.flights}
                tripData={tripData}
                tripId={trip.id}
                itemType="flights"
              />

              <ItemSection
                title="Hotels"
                icon={<Hotel className="h-4 w-4 text-orange-500" />}
                items={groupedAttachments.hotels}
                tripData={tripData}
                tripId={trip.id}
                itemType="hotels"
              />

              <ItemSection
                title="Car Rentals"
                icon={<Car className="h-4 w-4 text-green-500" />}
                items={groupedAttachments.cars}
                tripData={tripData}
                tripId={trip.id}
                itemType="cars"
              />

              <ItemSection
                title="Cruises"
                icon={<Ship className="h-4 w-4 text-purple-500" />}
                items={groupedAttachments.cruises}
                tripData={tripData}
                tripId={trip.id}
                itemType="cruises"
              />

              <ItemSection
                title="Activities"
                icon={<Calendar className="h-4 w-4 text-pink-500" />}
                items={groupedAttachments.activities}
                tripData={tripData}
                tripId={trip.id}
                itemType="activities"
              />
            </>
          )}
        </div>

        {/* Upload Section */}
        <div className="flex-shrink-0 pt-4 border-t">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
          />
          <Button
            variant="outline"
            className="w-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadAttachment.isPending}
          >
            {uploadAttachment.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Attachment
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            PDF, images, or Word docs up to 10MB
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
